import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import { Pagination, Table } from 'antd';
import { withWindowSize } from 'air-with-window-size';
import { PencilIcon, TrashIcon } from '../../../../../common/icons';
import SmallButton from './SmallButton';

import './DriversTable.css';

const pageSizeDefault = 7;
const pageSizeAlternative = 5;

@inject('commonStore', 'translationsStore')
@observer
class DriversTable extends Component {
  constructor(props) {
    super(props);
    this.defaultPageSize = 1;
    const dataSource = this.buildDataSourceWithKeys(this.props.drivers);
    const pageSize = this.defaultPageSize;

    let pagination = false;
    let paginationText = '';
    this.state = {
      dataSourceBuffer: dataSource,
      selectedRowKeys: [],
      pageSize,
      pagination,
      paginationText
    };
  }

  componentDidMount = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.drivers);
    if (!isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
  };

  componentDidUpdate(prevProps) {
    const someSizeChanged = prevProps.componentHeight !== this.props.componentHeight || prevProps.windowHeight !== this.props.windowHeight;
    const dataSource = this.buildDataSourceWithKeys(this.props.drivers);
    if (someSizeChanged || !isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
  }

  helperPageSize = () => {
    const defaultDocHeight = 800;
    if (this.props.documentHeight === defaultDocHeight) {
      return !this.props.group ? pageSizeDefault : pageSizeAlternative;
    } else {
      return !this.props.group
        ? Math.floor((this.props.documentHeight * pageSizeDefault) / defaultDocHeight)
        : Math.floor((this.props.documentHeight * pageSizeAlternative) / defaultDocHeight);
    }
  };

  changePageSize = pageSize => {
    this.setState({ pageSize });
  };

  helperPaginationText = (start, end, length) => {
    const { translationsStore } = this.props;
    if (length === 0) return `0 ${translationsStore.translate('drivers')}`;
    else if (length === 1) return `1 ${translationsStore.translate('driver')}`;
    else return `${start}-${end} ${translationsStore.translate('of')} ${length} ${translationsStore.translate('drivers')}`;
  };

  buildDataSourceWithKeys = collection => collection.map((item, index) => ({ ...item, key: index }));

  buildSelectedRowKeys = (dataSource, selectedRows) => {
    let selectedRowKeys = [];
    dataSource.forEach((row, index) => {
      if (typeof selectedRows.find(selected => selected.uuid === row.uuid) !== 'undefined') selectedRowKeys.push(index);
    });
    return selectedRowKeys;
  };

  toggleSelectAllRows = () => {
    const { drivers, onSelectedRowsChange } = this.props;
    let { selectedRows } = this.props;

    const dataSource = this.buildDataSourceWithKeys(drivers);
    let selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);

    if (selectedRowKeys.length > 0 && selectedRowKeys.length === dataSource.length) {
      selectedRows = [];
    } else {
      selectedRows = dataSource;
    }
    selectedRowKeys = selectedRows.map(row => row.key);
    onSelectedRowsChange(selectedRows);
    this.setState({ selectedRowKeys });
  };

  toggleSelectRow = data => {
    const { drivers, onSelectedRowsChange } = this.props;
    let { selectedRows } = this.props;

    const foundIndex = selectedRows.findIndex(selected => selected.id === data.id);
    if (foundIndex === -1) {
      selectedRows.push(data);
    } else {
      selectedRows.splice(foundIndex, 1);
    }
    onSelectedRowsChange(selectedRows);

    const dataSource = this.buildDataSourceWithKeys(drivers);
    const selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);
    this.setState({ selectedRowKeys });
  };

  buildPagination = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.drivers);
    const pageSize = this.helperPageSize();
    const pagination = {
      total: dataSource.length,
      defaultCurrent: 1,
      current: 1,
      // hideOnSinglePage: true,
      pageSize,
      simple: true,
      size: 'small',
      onChange: this.onPageChange
    };
    const start = 1;
    const end = Math.min(pagination.pageSize, pagination.total);

    const paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pageSize,
      pagination: { ...pagination },
      paginationText
    });
  };

  onPageChange = (page, pageSize) => {
    const dataSource = this.buildDataSourceWithKeys(this.props.drivers);
    const { pagination } = this.state;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, pagination.total);
    let paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pagination: { ...pagination, current: page },
      paginationText
    });
  };

  onRowEdit = data => {
    isFunction(this.props.onRowEdit) && this.props.onRowEdit(data);
  };

  onRowDelete = data => {
    isFunction(this.props.onRowDelete) && this.props.onRowDelete(data);
  };

  render() {
    const { drivers, selectedRows, commonStore, translationsStore } = this.props;

    const dataSource = this.buildDataSourceWithKeys(drivers);
    const selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);

    let checkmarkHeaderClassName = 'CheckmarkHeader';
    if (selectedRowKeys.length > 0 && selectedRowKeys.length === dataSource.length) {
      checkmarkHeaderClassName = `${checkmarkHeaderClassName} CheckmarkHeaderSelected`;
    } else if (selectedRowKeys.length > 0) {
      checkmarkHeaderClassName = `${checkmarkHeaderClassName} CheckmarkHeaderIndeterminate`;
    }

    const columns = [
      {
        key: 'checkmark',
        dataIndex: 'checkmark',
        title: (
          <div className={checkmarkHeaderClassName} onClick={this.toggleSelectAllRows}>
            <span className="checkmark" />
          </div>
        ),
        render: (text, record) => {
          const checked = selectedRowKeys.findIndex(element => element === record.key) !== -1;
          return (
            <div className={`Checkmark${checked ? ' CheckmarkSelected' : ''}`} onClick={() => this.toggleSelectRow(record)}>
              <span className="checkmark" />
            </div>
          );
        }
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: translationsStore.translate('driversManager.driversTable.headerLabels.name'),
        render: (text, record) => `${record.name} ${record.surname}`
      },
      {
        key: 'status',
        dataIndex: 'status',
        className: 'ColumnStatus',
        title: translationsStore.translate('driversManager.driversTable.headerLabels.status'),
        render: (text, record) => {
          const cls = classNames({
            CellStatus: true,
            [record.managementStatus]: true
          });
          return <div className={cls}>{translationsStore.translate(`driversManager.status.${record.managementStatus}`)}</div>;
        }
      },
      {
        key: 'mobile',
        dataIndex: 'mobile',
        title: translationsStore.translate('driversManager.driversTable.headerLabels.mobile'),
        render: (text, record) => (record.mobile ? record.mobile : '-')
      },
      {
        key: 'email',
        dataIndex: 'email',
        title: translationsStore.translate('driversManager.driversTable.headerLabels.email'),
        render: (text, record) => (record.email ? record.email : '-')
      },
      {
        key: 'assignedVehicle',
        dataIndex: 'assignedVehicle',
        className: 'ColumnCentered',
        title: translationsStore.translate('driversManager.driversTable.headerLabels.assignedVehicle'),
        render: (text, record) => {
          if (record.assignedVehicle) {
            return record.assignedVehicle.plate;
          } else {
            const clsAssign = classNames({
              button: true
            });
            return (
              <div className="buttonsContainer">
                <div className={clsAssign} onClick={() => this.onRowEdit(record)}>
                  {translationsStore.translate('driversManager.driversTable.actions.labelAssignVehicle')}
                </div>
              </div>
            );
          }
        }
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        title: '',
        render: (text, record) => {
          return (
            <div className="buttonsContainer">
              <SmallButton style={{ backgroundColor: 'var(--lock-on)' }} onClick={() => this.onRowEdit(record)}>
                <PencilIcon />
              </SmallButton>
              {!record.assignedVehicle && (
                <SmallButton style={{ backgroundColor: 'var(--red-dtc-color)' }} onClick={() => this.onRowDelete(record)}>
                  <TrashIcon />
                </SmallButton>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <div className="DriversTable">
        <Table columns={columns} dataSource={dataSource} pagination={this.state.pagination} rowClassName="ClientRow" />
        {!commonStore.loading && (
          <div className="DriversTablePagination">
            <div>{this.state.paginationText}</div>
            {dataSource.length > this.helperPageSize() && <Pagination {...this.state.pagination} />}
          </div>
        )}
      </div>
    );
  }
}

export default withWindowSize(DriversTable);



// WEBPACK FOOTER //
// ./src/components/App/DriversManagerDashboard/SelectableDrivers/DriversTable/DriversTable.js